import * as SliderPrimitive from '@radix-ui/react-slider';
import { useEffect, useState, type ReactNode } from 'react';
import { resolveRealtyFilterRangeBounds } from '../../realty-filter-recipe';
import styles from '../../realty-filter.module.css';

type NumericRange = readonly [number, number];

type RealtyFilterRangeProps = {
  ariaLabel: string;
  bounds: NumericRange;
  formatAccessibleValue: (value: number) => string;
  renderValue: (value: number) => ReactNode;
  rangeLabels: readonly [string, string];
  unboundedLabel: string;
  step: number;
  value: readonly [number | undefined, number | undefined];
  onCommit: (value: NumericRange, effectiveBounds: NumericRange) => void;
};

const clamp = (value: number, minimum: number, maximum: number) =>
  Math.min(maximum, Math.max(minimum, value));

const normalizeRange = (
  value: readonly [number | undefined, number | undefined],
  bounds: NumericRange,
): [number, number] => {
  const [minimum, maximum] = bounds;
  const from = clamp(value[0] ?? minimum, minimum, maximum);
  const to = clamp(value[1] ?? maximum, minimum, maximum);

  return from <= to ? [from, to] : [to, from];
};

export const RealtyFilterRange = ({
  ariaLabel,
  bounds,
  formatAccessibleValue,
  renderValue,
  rangeLabels,
  unboundedLabel,
  step,
  value,
  onCommit,
}: RealtyFilterRangeProps) => {
  const [from, to] = value;
  const effectiveBounds = resolveRealtyFilterRangeBounds(bounds, value, step);
  const [minimum, maximum] = effectiveBounds;
  const [range, setRange] = useState<[number, number]>(() =>
    normalizeRange(value, effectiveBounds),
  );
  const upperIsUnbounded = to === undefined && range[1] === maximum;

  useEffect(() => {
    setRange(normalizeRange([from, to], [minimum, maximum]));
  }, [from, maximum, minimum, to]);

  return (
    <div className={styles.rangeControl}>
      <div className={styles.rangeInputs}>
        <output
          className={styles.rangeInput}
          aria-label={`${ariaLabel}: ${formatAccessibleValue(range[0])}`}
        >
          {renderValue(range[0])}
        </output>
        <output
          className={styles.rangeInput}
          aria-label={`${ariaLabel}: ${upperIsUnbounded ? unboundedLabel : formatAccessibleValue(range[1])}`}
          data-unbounded={upperIsUnbounded ? 'true' : 'false'}
          title={upperIsUnbounded ? unboundedLabel : undefined}
        >
          {renderValue(range[1])}
        </output>
      </div>
      <SliderPrimitive.Root
        value={range}
        min={minimum}
        max={maximum}
        step={step}
        onValueChange={nextRange =>
          setRange(normalizeRange([nextRange[0], nextRange[1]], effectiveBounds))
        }
        onValueCommit={nextRange =>
          onCommit(normalizeRange([nextRange[0], nextRange[1]], effectiveBounds), effectiveBounds)
        }
        className={styles.rangeSlider}
      >
        <SliderPrimitive.Track className={styles.rangeTrack}>
          <SliderPrimitive.Range className={styles.rangeSelection} />
        </SliderPrimitive.Track>
        <SliderPrimitive.Thumb
          aria-label={`${ariaLabel} ${rangeLabels[0]}`}
          aria-valuetext={formatAccessibleValue(range[0])}
          className={styles.rangeThumb}
        />
        <SliderPrimitive.Thumb
          aria-label={`${ariaLabel} ${rangeLabels[1]}`}
          aria-valuetext={upperIsUnbounded ? unboundedLabel : formatAccessibleValue(range[1])}
          className={styles.rangeThumb}
        />
      </SliderPrimitive.Root>
    </div>
  );
};
